/**
 * MarkdownViewer Component
 *
 * Lightweight markdown renderer with LaTeX math support
 * Used for readOnly mode in BrainlyMathEditor
 */

import { type FC, useMemo, CSSProperties } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkMath from 'remark-math'
import rehypeKatex from 'rehype-katex'
import rehypeRaw from 'rehype-raw'
import 'katex/dist/katex.min.css'
import {
  detectContentFormat,
  htmlToMarkdown,
  unescapeMarkdown,
} from '../../../utils/markdownConverter'

interface MarkdownViewerProps {
  /**
   * Content in Markdown or HTML format (auto-detected)
   * Will be converted to markdown if HTML is detected
   */
  content: string
  /**
   * Height of the viewer container
   */
  height?: string | number
  /**
   * Minimum height of the viewer container
   */
  minHeight?: string | number
  /**
   * CSS class name
   */
  className?: string
}

const MarkdownViewer: FC<MarkdownViewerProps> = ({
  content,
  height = '300px',
  minHeight = '200px',
  className = '',
}) => {
  // Auto-detect format and convert HTML to markdown if needed
  const markdownContent = useMemo(() => {
    if (!content || content.trim() === '') {
      return ''
    }

    // Normalize markdown lists - remove blank lines between consecutive list items
    // BUT preserve blank lines before the first list item (required by react-markdown)
    const normalizeMarkdownLists = (text: string): string => {
      const lines = text.split('\n')
      const result: string[] = []
      let previousWasListItem = false
      let insideList = false

      lines.forEach((line, index) => {
        const isOrderedListItem = /^\d+\.\s+/.test(line)
        const isUnorderedListItem = /^[-*+]\s+/.test(line)
        const isListItem = isOrderedListItem || isUnorderedListItem
        const isBlank = line.trim() === ''

        // Check if next line is a list item
        const nextLine = lines[index + 1]
        const nextIsListItem =
          nextLine && (/^\d+\.\s+/.test(nextLine) || /^[-*+]\s+/.test(nextLine))

        // Entering a list
        if (isListItem && !insideList) {
          insideList = true
          // Ensure blank line before first list item if previous line exists and is not blank
          if (result.length > 0 && result[result.length - 1].trim() !== '') {
            result.push('') // Add blank line before list
          }
          result.push(line)
          previousWasListItem = true
          return
        }

        // Inside list
        if (isListItem && insideList) {
          result.push(line)
          previousWasListItem = true
          return
        }

        // Exiting list (blank or non-list after list item)
        if (!isListItem && insideList) {
          insideList = false
          // Skip blank line right after list ends
          if (isBlank) {
            previousWasListItem = false
            return
          }
        }

        // Skip blank lines between list items
        if (isBlank && previousWasListItem && nextIsListItem) {
          return // Skip this blank line
        }

        result.push(line)
        previousWasListItem = isListItem
      })

      return result.join('\n')
    }

    // First unescape any escaped markdown syntax
    const unescaped = unescapeMarkdown(content)

    // Detect format on unescaped content
    const format = detectContentFormat(unescaped)

    let markdown = unescaped

    // If it's HTML, convert to markdown first
    if (format === 'html') {
      markdown = htmlToMarkdown(unescaped)
    }

    // Normalize lists to remove blank lines between items
    markdown = normalizeMarkdownLists(markdown)

    return markdown
  }, [content])
  const containerStyle: CSSProperties = {
    height: typeof height === 'number' ? `${height}px` : height,
    minHeight: typeof minHeight === 'number' ? `${minHeight}px` : minHeight,
    overflow: 'auto',
    padding: '16px',
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    backgroundColor: '#fafafa',
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    fontSize: '14px',
    lineHeight: '1.6',
    color: '#333',
  }

  return (
    <div className={`markdown-viewer ${className}`} style={containerStyle}>
      <ReactMarkdown
        remarkPlugins={[remarkMath as any]}
        rehypePlugins={[rehypeKatex as any, rehypeRaw as any]}
        components={{
          // Custom rendering for better styling
          p: ({ children }) => (
            <p style={{ marginBottom: '12px', marginTop: 0 }}>{children}</p>
          ),
          h1: ({ children }) => (
            <h1
              style={{
                fontSize: '24px',
                fontWeight: 600,
                marginBottom: '16px',
                marginTop: '24px',
              }}
            >
              {children}
            </h1>
          ),
          h2: ({ children }) => (
            <h2
              style={{
                fontSize: '20px',
                fontWeight: 600,
                marginBottom: '12px',
                marginTop: '20px',
              }}
            >
              {children}
            </h2>
          ),
          h3: ({ children }) => (
            <h3
              style={{
                fontSize: '18px',
                fontWeight: 600,
                marginBottom: '12px',
                marginTop: '16px',
              }}
            >
              {children}
            </h3>
          ),
          ul: ({ children }) => (
            <ul
              style={{
                marginBottom: '12px',
                paddingLeft: '24px',
                listStyleType: 'disc',
              }}
            >
              {children}
            </ul>
          ),
          ol: ({ children }) => (
            <ol
              style={{
                marginBottom: '12px',
                paddingLeft: '24px',
                listStyleType: 'decimal',
              }}
            >
              {children}
            </ol>
          ),
          li: ({ children }) => (
            <li style={{ marginBottom: '4px' }}>{children}</li>
          ),
          strong: ({ children }) => (
            <strong style={{ fontWeight: 600 }}>{children}</strong>
          ),
          em: ({ children }) => (
            <em style={{ fontStyle: 'italic' }}>{children}</em>
          ),
          code: ({ children, className }) => {
            // Check if it's inline code by checking className
            const isInline = !className || !className.startsWith('language-')
            return isInline ? (
              <code
                style={{
                  backgroundColor: '#f5f5f5',
                  padding: '2px 6px',
                  borderRadius: '3px',
                  fontFamily: 'monospace',
                  fontSize: '13px',
                }}
              >
                {children}
              </code>
            ) : (
              <code
                className={className}
                style={{
                  display: 'block',
                  backgroundColor: '#f5f5f5',
                  padding: '12px',
                  borderRadius: '4px',
                  fontFamily: 'monospace',
                  fontSize: '13px',
                  overflow: 'auto',
                  marginBottom: '12px',
                }}
              >
                {children}
              </code>
            )
          },
          blockquote: ({ children }) => (
            <blockquote
              style={{
                borderLeft: '4px solid #e0e0e0',
                paddingLeft: '16px',
                marginLeft: 0,
                marginBottom: '12px',
                color: '#666',
                fontStyle: 'italic',
              }}
            >
              {children}
            </blockquote>
          ),
          img: ({ src, alt }) => (
            <div
              style={{
                maxWidth: '400px',
                maxHeight: '300px',
                display: 'block',
                marginBottom: '12px',
                overflow: 'hidden',
              }}
            >
              <img
                src={src}
                alt={alt}
                style={{
                  maxWidth: '100%',
                  maxHeight: '300px',
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain',
                  display: 'block',
                  borderRadius: '4px',
                }}
              />
            </div>
          ),
          a: ({ href, children }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: '#0066cc',
                textDecoration: 'none',
              }}
            >
              {children}
            </a>
          ),
          table: ({ children }) => (
            <table
              style={{
                borderCollapse: 'collapse',
                width: '100%',
                marginBottom: '12px',
              }}
            >
              {children}
            </table>
          ),
          th: ({ children }) => (
            <th
              style={{
                border: '1px solid #e0e0e0',
                padding: '8px',
                backgroundColor: '#f5f5f5',
                fontWeight: 600,
                textAlign: 'left',
              }}
            >
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td
              style={{
                border: '1px solid #e0e0e0',
                padding: '8px',
              }}
            >
              {children}
            </td>
          ),
        }}
      >
        {markdownContent}
      </ReactMarkdown>
    </div>
  )
}

export default MarkdownViewer
